/**
 * AI Brand Profile Type Definitions
 *
 * Types for AI brand profile management
 * Enhanced with comprehensive brand profiling fields
 */

// ============================================================================
// Nested Types
// ============================================================================

export interface Demographics {
  ageRange?: string;
  gender?: string;
  incomeLevel?: string;
  education?: string;
}

export interface Psychographics {
  interests: string[];
  values: string[];
  painPoints: string[];
  goals: string[];
}

export interface Audience {
  id: string;
  name: string;
  demographics?: Demographics;
  psychographics?: Psychographics;
  territories: string[];
  preferredPlatforms: string[];
}

export interface ProductService {
  id: string;
  name: string;
  description?: string;
  category?: string;
}

export interface Competitor {
  name: string;
  positioning?: string;
}

export interface CoreValue {
  value: string;
  description?: string;
}

export interface AvoidTopic {
  topic: string;
  reason?: string;
}

export interface WritingStyle {
  sentenceLength?: 'short' | 'medium' | 'long' | 'varied';
  useActiveVoice?: boolean;
  useContractions?: boolean;
  useMetaphors?: boolean;
}

// ============================================================================
// Enum Types
// ============================================================================

export type PricePositioning = 'premium' | 'mid-market' | 'value' | 'budget';

export type EmotionalTone =
  | 'inspirational'
  | 'informative'
  | 'entertaining'
  | 'persuasive'
  | 'empathetic';

export type CompetitorMentionPolicy =
  | 'never_mention'
  | 'mention_neutrally'
  | 'compare_positively';

export type HashtagStyle = 'branded' | 'trending' | 'mixed';

export type DefaultLength = 'short' | 'medium' | 'long';

// ============================================================================
// Main Types
// ============================================================================

export type AIBrandProfileStatus = 'DRAFT' | 'ACTIVE';

export interface AIBrandProfile {
  id: number;
  teamId: number;
  name: string;
  isDefault: boolean;
  status: AIBrandProfileStatus;

  // Section 1: Brand Setup
  companyName: string | null;
  industry: string | null;
  missionStatement: string | null;
  visionStatement: string | null;
  brandStory: string | null;
  uniqueSellingProposition: string | null;
  tagline: string | null;

  // Section 2: Audiences
  targetAudience: string | null; // Deprecated but kept for compatibility
  audiences: Audience[];

  // Section 3: Playing Field + Offer
  productsServices: ProductService[];
  valueProposition: string | null;
  keyDifferentiators: string[];
  competitors: Competitor[];
  pricePositioning: PricePositioning | null;

  // Section 4: Brand Values
  coreValues: CoreValue[];
  brandPersonality: string[];
  brandPrinciples: string | null;

  // Section 5: Voice & Tonality
  brandVoice: string | null;
  voiceCharacteristics: string[];
  emotionalTone: EmotionalTone | null;
  writingStyle: WritingStyle;
  tone: string | null;
  formality: number | null;
  language: string | null;

  // Section 6: Nope List
  avoidWords: string[];
  avoidTopics: AvoidTopic[];
  sensitiveSubjects: string[];
  competitorMentionPolicy: CompetitorMentionPolicy | null;
  contentRestrictions: string | null;

  // Content Preferences
  defaultLength: DefaultLength | null;
  includeEmojis: boolean;
  includeHashtags: boolean;
  hashtagStyle: HashtagStyle | null;
  defaultCta: string | null;
  samplePosts: string[];

  // Metadata
  createdBy: number | null;
  createdAt: string;
  updatedAt: string;

  // Usage Tracking
  usageCount: number;
  lastUsedAt: string | null;
}

export interface CreateAIBrandProfileInput {
  name: string;

  /** DRAFT profiles are team-visible but excluded from generation pickers and can't be set default. Defaults to ACTIVE server-side. */
  status?: AIBrandProfileStatus;

  // Section 1: Brand Setup
  companyName?: string;
  industry?: string;
  missionStatement?: string;
  visionStatement?: string;
  brandStory?: string;
  uniqueSellingProposition?: string;
  tagline?: string;

  // Section 2: Audiences
  targetAudience?: string;
  audiences?: Audience[];

  // Section 3: Playing Field + Offer
  productsServices?: ProductService[];
  valueProposition?: string;
  keyDifferentiators?: string[];
  competitors?: Competitor[];
  pricePositioning?: PricePositioning;

  // Section 4: Brand Values
  coreValues?: CoreValue[];
  brandPersonality?: string[];
  brandPrinciples?: string;

  // Section 5: Voice & Tonality
  brandVoice?: string;
  voiceCharacteristics?: string[];
  emotionalTone?: EmotionalTone;
  writingStyle?: WritingStyle;
  tone?: string;
  formality?: number;
  language?: string;

  // Section 6: Nope List
  avoidWords?: string[];
  avoidTopics?: AvoidTopic[];
  sensitiveSubjects?: string[];
  competitorMentionPolicy?: CompetitorMentionPolicy;
  contentRestrictions?: string;

  // Content Preferences
  defaultLength?: DefaultLength;
  includeEmojis?: boolean;
  includeHashtags?: boolean;
  hashtagStyle?: HashtagStyle;
  defaultCta?: string;
  samplePosts?: string[];
}

export type UpdateAIBrandProfileInput = Partial<CreateAIBrandProfileInput>;

// ============================================================================
// API Response Types
// ============================================================================

export interface AIBrandProfileListResponse {
  success: boolean;
  data: {
    profiles: AIBrandProfile[];
  };
}

export interface AIBrandProfileResponse {
  success: boolean;
  data: AIBrandProfile;
}

/**
 * A single snapshot in a brand profile's edit history.
 * Created automatically by the server on every update; restorable.
 */
export interface AIBrandProfileVersion {
  id: number;
  profileId: number;
  versionNumber: number;
  data: Record<string, unknown>;
  note: string | null;
  createdBy: number | null;
  createdAt: string;
}

// ============================================================================
// Server-side Draft Types (Create Wizard resume-across-devices)
// ============================================================================

/**
 * Server-persisted draft for the "Create AI Brand Profile" wizard.
 * Backed by GET/PUT/DELETE /api/ai/brand-profiles/draft.
 */
export interface AIBrandProfileDraft {
  draftData: AIBrandProfileFormData;
  step: number;
  updatedAt: string;
}

export interface AIBrandProfileDraftResponse {
  success: boolean;
  data: AIBrandProfileDraft | null;
}

export interface SaveAIBrandProfileDraftInput {
  draftData: AIBrandProfileFormData;
  step: number;
}

// ============================================================================
// Form Data Type (for UI state management)
// ============================================================================

export interface AIBrandProfileFormData {
  // Section 1: Brand Setup
  name: string;
  companyName: string;
  industry: string;
  missionStatement: string;
  visionStatement: string;
  brandStory: string;
  uniqueSellingProposition: string;
  tagline: string;

  // Section 2: Audiences
  audiences: Audience[];

  // Section 3: Playing Field + Offer
  productsServices: ProductService[];
  valueProposition: string;
  keyDifferentiators: string[];
  competitors: Competitor[];
  pricePositioning: PricePositioning;

  // Section 4: Brand Values
  coreValues: CoreValue[];
  brandPersonality: string[];
  brandPrinciples: string;

  // Section 5: Voice & Tonality
  brandVoice: string;
  voiceCharacteristics: string[];
  emotionalTone: EmotionalTone | '';
  writingStyle: WritingStyle;
  tone: string;
  formality: number;
  language: string;

  // Section 6: Nope List
  avoidWords: string[];
  avoidTopics: AvoidTopic[];
  sensitiveSubjects: string[];
  competitorMentionPolicy: CompetitorMentionPolicy;
  contentRestrictions: string;

  // Content Preferences
  defaultLength: DefaultLength;
  includeEmojis: boolean;
  includeHashtags: boolean;
  hashtagStyle: HashtagStyle;
  defaultCta: string;
  samplePosts: string[];
}

// ============================================================================
// Constants for UI
// ============================================================================

export const PRICE_POSITIONING_OPTIONS: { value: PricePositioning; label: string }[] = [
  { value: 'premium', label: 'Premium' },
  { value: 'mid-market', label: 'Mid-Market' },
  { value: 'value', label: 'Value' },
  { value: 'budget', label: 'Budget' },
];

export const EMOTIONAL_TONE_OPTIONS: { value: EmotionalTone; label: string }[] = [
  { value: 'inspirational', label: 'Inspirational' },
  { value: 'informative', label: 'Informative' },
  { value: 'entertaining', label: 'Entertaining' },
  { value: 'persuasive', label: 'Persuasive' },
  { value: 'empathetic', label: 'Empathetic' },
];

export const COMPETITOR_POLICY_OPTIONS: { value: CompetitorMentionPolicy; label: string }[] = [
  { value: 'never_mention', label: 'Never Mention' },
  { value: 'mention_neutrally', label: 'Mention Neutrally' },
  { value: 'compare_positively', label: 'Compare Positively' },
];

export const TONE_OPTIONS = [
  { value: 'professional', label: 'Professional' },
  { value: 'casual', label: 'Casual' },
  { value: 'friendly', label: 'Friendly' },
  { value: 'funny', label: 'Funny' },
  { value: 'formal', label: 'Formal' },
];

export const LENGTH_OPTIONS: { value: DefaultLength; label: string }[] = [
  { value: 'short', label: 'Short' },
  { value: 'medium', label: 'Medium' },
  { value: 'long', label: 'Long' },
];

export const HASHTAG_STYLE_OPTIONS: { value: HashtagStyle; label: string }[] = [
  { value: 'branded', label: 'Branded' },
  { value: 'trending', label: 'Trending' },
  { value: 'mixed', label: 'Mixed' },
];

export const WRITING_STYLE_SENTENCE_LENGTH_OPTIONS = [
  { value: 'short', label: 'Short' },
  { value: 'medium', label: 'Medium' },
  { value: 'long', label: 'Long' },
  { value: 'varied', label: 'Varied' },
];

/**
 * Brand Personality Traits (based on Aaker's Brand Personality Model)
 */
export const BRAND_PERSONALITY_TRAITS = {
  sincerity: [
    { value: 'down-to-earth', label: 'Down-to-earth' },
    { value: 'honest', label: 'Honest' },
    { value: 'wholesome', label: 'Wholesome' },
    { value: 'cheerful', label: 'Cheerful' },
  ],
  excitement: [
    { value: 'daring', label: 'Daring' },
    { value: 'spirited', label: 'Spirited' },
    { value: 'imaginative', label: 'Imaginative' },
    { value: 'up-to-date', label: 'Up-to-date' },
  ],
  competence: [
    { value: 'reliable', label: 'Reliable' },
    { value: 'intelligent', label: 'Intelligent' },
    { value: 'successful', label: 'Successful' },
    { value: 'leader', label: 'Leader' },
  ],
  sophistication: [
    { value: 'upper-class', label: 'Upper-class' },
    { value: 'charming', label: 'Charming' },
    { value: 'elegant', label: 'Elegant' },
    { value: 'prestigious', label: 'Prestigious' },
  ],
  ruggedness: [
    { value: 'outdoorsy', label: 'Outdoorsy' },
    { value: 'tough', label: 'Tough' },
    { value: 'strong', label: 'Strong' },
    { value: 'rugged', label: 'Rugged' },
  ],
};

/**
 * Voice Characteristics Options
 */
export const VOICE_CHARACTERISTICS_OPTIONS = [
  { value: 'confident', label: 'Confident' },
  { value: 'approachable', label: 'Approachable' },
  { value: 'authoritative', label: 'Authoritative' },
  { value: 'empathetic', label: 'Empathetic' },
  { value: 'witty', label: 'Witty' },
  { value: 'bold', label: 'Bold' },
  { value: 'warm', label: 'Warm' },
  { value: 'expert', label: 'Expert' },
  { value: 'innovative', label: 'Innovative' },
  { value: 'playful', label: 'Playful' },
];

/**
 * Default form data for creating a new profile
 */
export const DEFAULT_PROFILE_FORM_DATA: AIBrandProfileFormData = {
  name: '',
  companyName: '',
  industry: '',
  missionStatement: '',
  visionStatement: '',
  brandStory: '',
  uniqueSellingProposition: '',
  tagline: '',
  audiences: [],
  productsServices: [],
  valueProposition: '',
  keyDifferentiators: [],
  competitors: [],
  pricePositioning: 'mid-market',
  coreValues: [],
  brandPersonality: [],
  brandPrinciples: '',
  brandVoice: '',
  voiceCharacteristics: [],
  emotionalTone: '',
  writingStyle: {},
  tone: '',
  formality: 5,
  language: 'English',
  avoidWords: [],
  avoidTopics: [],
  sensitiveSubjects: [],
  competitorMentionPolicy: 'never_mention',
  contentRestrictions: '',
  defaultLength: 'medium',
  includeEmojis: false,
  includeHashtags: true,
  hashtagStyle: 'mixed',
  defaultCta: '',
  samplePosts: [],
};
